"use client";

import { LazyMotion, domAnimation, MotionConfig } from "motion/react";

/**
 * Root motion provider.
 *
 * LazyMotion loads ONLY the domAnimation feature set (animations, variants,
 * exit, hover/tap/focus gestures) — the app uses no layout or drag props, so
 * domMax is unnecessary. Every animated element renders via the lightweight `m`
 * component instead of the full `motion` component, which cannot tree-shake
 * below ~34KB because its prop API statically pulls every feature. This trims
 * the framer-motion runtime in the initial bundle to ~20KB site-wide.
 *
 * `strict` makes any stray full `motion` component throw at render, which
 * guarantees the saving holds — mixing even one `motion` would reload the full
 * feature set and defeat LazyMotion.
 *
 * MotionConfig reducedMotion="user" honours the OS prefers-reduced-motion
 * setting for every declarative animation in the tree (animate / whileInView /
 * whileHover / transition) — transform + layout animations are disabled while
 * opacity is preserved — with zero per-component branching. (MotionValue-bound
 * styles such as the scroll-scrubbers are not "animations" in motion's sense
 * and are handled separately; the Signal thread keeps its own freeze-to-end
 * reduced-motion contract via useSignalProgress.)
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}
